/**
 * The orcaloops sign-in door: a v5 {@link Door} card driven by Clerk's
 * headless sign-in API (`useSignIn`) instead of Clerk's `<SignIn>` UI.
 *
 * Two steps. `email`: the address and "Continue with Google" — the instance's
 * first factors are `email_code` and `oauth_google`, nothing else. `code`: the
 * six-digit code Clerk emails; a `complete` sign-in activates its session and
 * lands the person on the door's redirect.
 *
 * Two routes render this door (`/orca/login/` and its `$` catch-all), so it
 * reads the search with `useSearch({ strict: false })`, never one route's
 * `Route.useSearch`.
 *
 * Clerk's promise-returning calls are lifted through {@link clerkTryPromise}
 * and the flows run as `Atom.fn` setters — no `async`/`await`, no `useState`,
 * no `useEffect`.
 *
 * @since 1.0.0
 */
import { useSignIn } from "@clerk/tanstack-react-start";
import { Atom, useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Effect, Match, Option } from "effect";
import type { FormEvent, ReactNode } from "react";

import { clerkTryPromise } from "~/lib/clerk-effect.ts";
import { type LoginSearch, validateLoginSearch } from "~/lib/login-search.ts";
import {
  clerkErrorMessage,
  DOOR_STRATEGY,
  doorRedirect,
  emailCodeFactor,
  GOOGLE_STRATEGY,
  oauthRedirectUrls,
} from "~/lib/orcaloops-clerk.ts";

import { Door, OAuthButton } from "./Door.tsx";
import { Field, Input } from "./Form.tsx";
import { OrcaloopsFrame, ThemeToggle, TopCrumbs, TopGrow, TopMark } from "./OrcaloopsFrame.tsx";
import { Btn } from "./primitives.tsx";

/** The loaded branch of `useSignIn()`, derived so it tracks Clerk's own types. */
type LoadedSignIn = Extract<ReturnType<typeof useSignIn>, { readonly isLoaded: true }>;

/**
 * The slice of Clerk's sign-in resource the door drives, as `useSignIn()`
 * hands it over once loaded.
 *
 * @since 1.0.0
 */
export type SignInResource = Pick<
  LoadedSignIn["signIn"],
  "create" | "prepareFirstFactor" | "attemptFirstFactor" | "authenticateWithRedirect"
>;

/** Clerk's `setActive`, as `useSignIn()` hands it over once loaded. @since 1.0.0 */
export type SignInSetActive = LoadedSignIn["setActive"];

/** The door's two steps. @since 1.0.0 */
export type LoginStep = "email" | "code";

/** What every sign-in flow closes over. @since 1.0.0 */
export interface LoginDeps {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly search: LoginSearch;
  readonly setActive: SignInSetActive;
  readonly setBusy: (busy: boolean) => void;
  readonly setError: (error: string | null) => void;
  readonly setStep: (step: LoginStep) => void;
  readonly signIn: SignInResource;
}

/** The error when Clerk offers no email code for the address. @since 1.0.0 */
export const NO_EMAIL_CODE = "Email code sign-in is not available for this address";

/** The error when the code did not complete the sign-in. @since 1.0.0 */
export const CODE_REJECTED = "That code did not work. Try again.";

/** The door's state. @since 1.0.0 */
export const loginStepAtom = Atom.make<LoginStep>("email");
/** @since 1.0.0 */
export const loginEmailAtom = Atom.make("");
/** @since 1.0.0 */
export const loginCodeAtom = Atom.make("");
/** @since 1.0.0 */
export const loginErrorAtom = Atom.make<string | null>(null);
/** @since 1.0.0 */
export const loginBusyAtom = Atom.make(false);

/**
 * Run one Clerk step with the busy flag raised and the error cleared; a
 * rejection anywhere in it becomes the door's error line, and busy drops on
 * every exit.
 */
const guarded = <E extends { readonly cause: unknown }>(
  deps: LoginDeps,
  step: Effect.Effect<void, E>,
): Effect.Effect<void> =>
  Effect.sync(() => {
    deps.setBusy(true);
    deps.setError(null);
  }).pipe(
    Effect.flatMap(() => step),
    Effect.catchAll((error) => Effect.sync(() => deps.setError(clerkErrorMessage(error.cause)))),
    Effect.ensuring(Effect.sync(() => deps.setBusy(false))),
  );

/**
 * Start the sign-in for an email, send the email code, and move to the `code`
 * step — or say so when Clerk offers no email code for the address.
 *
 * @since 1.0.0
 */
export const startEmail = (deps: LoginDeps, email: string): Effect.Effect<void> =>
  guarded(
    deps,
    clerkTryPromise(() => deps.signIn.create({ identifier: email })).pipe(
      Effect.flatMap((result) =>
        Option.match(emailCodeFactor(result.supportedFirstFactors), {
          onNone: () => Effect.sync(() => deps.setError(NO_EMAIL_CODE)),
          onSome: (factor) =>
            clerkTryPromise(() =>
              deps.signIn.prepareFirstFactor({ strategy: DOOR_STRATEGY, emailAddressId: factor.emailAddressId })
            ).pipe(Effect.flatMap(() => Effect.sync(() => deps.setStep("code")))),
        })
      ),
    ),
  );

/**
 * Attempt the emailed code. `complete` activates the session and lands the
 * person on the door's redirect; any other status is a code that did not work.
 *
 * @since 1.0.0
 */
export const verifyCode = (deps: LoginDeps, code: string): Effect.Effect<void> =>
  guarded(
    deps,
    clerkTryPromise(() => deps.signIn.attemptFirstFactor({ strategy: DOOR_STRATEGY, code })).pipe(
      Effect.flatMap((result) =>
        Match.value(result.status).pipe(
          Match.when("complete", () =>
            clerkTryPromise(() => deps.setActive({ session: result.createdSessionId })).pipe(
              Effect.flatMap(() => clerkTryPromise(() => deps.navigate({ to: doorRedirect(deps.search) }))),
            )),
          Match.orElse(() => Effect.sync(() => deps.setError(CODE_REJECTED))),
        )
      ),
    ),
  );

/**
 * Hand the browser to Google; the round-trip returns through
 * `/login/sso-callback`.
 *
 * @since 1.0.0
 */
export const startGoogle = (deps: LoginDeps): Effect.Effect<void> =>
  guarded(
    deps,
    clerkTryPromise(() =>
      deps.signIn.authenticateWithRedirect({ strategy: GOOGLE_STRATEGY, ...oauthRedirectUrls("login", deps.search) })
    ),
  );

/** The flows as atom setters, run through the effect-atom runtime. @since 1.0.0 */
export const startEmailFn = Atom.fn((args: { readonly deps: LoginDeps; readonly email: string }) =>
  startEmail(args.deps, args.email)
);
/** @since 1.0.0 */
export const verifyCodeFn = Atom.fn((args: { readonly code: string; readonly deps: LoginDeps }) =>
  verifyCode(args.deps, args.code)
);
/** @since 1.0.0 */
export const startGoogleFn = Atom.fn((deps: LoginDeps) => startGoogle(deps));

/**
 * The card and its two steps. See the module doc.
 *
 * @since 1.0.0
 */
export function LoginDoor(): ReactNode {
  const clerk = useSignIn();
  const navigate = useNavigate();
  const { redirect_url } = useSearch({ strict: false });
  const search = validateLoginSearch({ redirect_url });

  const step = useAtomValue(loginStepAtom);
  const setStep = useAtomSet(loginStepAtom);
  const email = useAtomValue(loginEmailAtom);
  const setEmail = useAtomSet(loginEmailAtom);
  const code = useAtomValue(loginCodeAtom);
  const setCode = useAtomSet(loginCodeAtom);
  const error = useAtomValue(loginErrorAtom);
  const setError = useAtomSet(loginErrorAtom);
  const busy = useAtomValue(loginBusyAtom);
  const setBusy = useAtomSet(loginBusyAtom);

  const runEmail = useAtomSet(startEmailFn);
  const runVerify = useAtomSet(verifyCodeFn);
  const runGoogle = useAtomSet(startGoogleFn);

  const deps: LoginDeps | null = clerk.isLoaded
    ? { navigate, search, setActive: clerk.setActive, setBusy, setError, setStep, signIn: clerk.signIn }
    : null;
  const disabled = deps === null || busy;

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (deps !== null) runEmail({ deps, email });
  };
  const submitCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (deps !== null) runVerify({ code, deps });
  };
  const back = () => {
    setError(null);
    setCode("");
    setStep("email");
  };

  return (
    <Door
      error={error === null ? undefined : error}
      footer={
        <>
          No account? <a href="/orca/sign-up">Create account</a>
        </>
      }
      subtitle="Outcomes in, evidence out"
      title="Sign in"
    >
      {step === "email"
        ? (
          <form className="ol-door-body" data-testid="login-form" onSubmit={submitEmail}>
            <Field label="Email">
              <Input
                autoComplete="email"
                autoFocus
                disabled={disabled}
                name="identifier"
                onChange={setEmail}
                testId="login-email"
                type="email"
                value={email}
              />
            </Field>
            <Btn disabled={disabled} testId="login-continue" type="submit" variant="fill">Continue</Btn>
            <div className="ol-or">or</div>
            <OAuthButton
              disabled={disabled}
              onClick={() => {
                if (deps !== null) runGoogle(deps);
              }}
              provider="google"
              testId="login-google"
            />
          </form>
        )
        : (
          <form className="ol-door-body" data-testid="login-code-form" onSubmit={submitCode}>
            <Field hint={`We sent a 6-digit code to ${email}`} label="Code">
              <Input
                autoComplete="one-time-code"
                autoFocus
                disabled={disabled}
                inputMode="numeric"
                name="code"
                onChange={setCode}
                testId="login-code"
                value={code}
              />
            </Field>
            <Btn disabled={disabled} testId="login-verify" type="submit" variant="fill">Sign in</Btn>
            <Btn disabled={busy} onClick={back} testId="login-back">Use a different email</Btn>
          </form>
        )}
    </Door>
  );
}

/**
 * The whole `/orca/login` page: the bar-only frame, one `<main>`, the door.
 *
 * @since 1.0.0
 */
export function LoginPage(): ReactNode {
  return (
    <OrcaloopsFrame
      top={
        <>
          <TopMark />
          <TopCrumbs crumbs={[{ label: "Sign in" }]} />
          <TopGrow />
          <ThemeToggle />
        </>
      }
    >
      <main style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <LoginDoor />
      </main>
    </OrcaloopsFrame>
  );
}
